package com.evoapps.evoshift.domain.legal

import com.evoapps.evoshift.domain.market.payroll.poland.PolishCalendar
import com.evoapps.evoshift.domain.model.SundayPolicy
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDate
import kotlinx.datetime.number
import kotlinx.datetime.plus

/** Why a workplace is closed on a day (SH-55). */
enum class ClosureReason {
    /** A Sunday that is not on the year's list of trading Sundays ([SundayPolicy.TRADING_SUNDAYS]). */
    NON_TRADING_SUNDAY,
    /** A public holiday on which trade is banned. */
    PUBLIC_HOLIDAY,
    /** Any Sunday ([SundayPolicy.CLOSED]). */
    SUNDAY
}

/** A day the workplace's [SundayPolicy] closes: no skeleton shifts, a marker in the roster and the daily log. */
data class DayClosure(
    val date: LocalDate,
    val reason: ClosureReason,
    /** The holiday's name in the statute ("Wszystkich Świętych"), for [ClosureReason.PUBLIC_HOLIDAY]. */
    val holidayName: String? = null
)

/**
 * The policy × date rules (SH-55). Pure: the calendar comes in. Without a calendar for the year
 * (no list written yet) a [SundayPolicy.TRADING_SUNDAYS] workplace is closed on every Sunday - the
 * Act's default; the trading Sundays are its exceptions and are never guessed here - and on the
 * public holidays the law itself fixes ([PolishCalendar]).
 */
object SundayPolicyRules {

    fun closure(date: LocalDate, policy: SundayPolicy, calendar: LegalCalendarYear?): DayClosure? {
        if (policy == SundayPolicy.OPEN) return null
        val holiday = holidayOn(date, calendar)
        if (holiday != null) return DayClosure(date, ClosureReason.PUBLIC_HOLIDAY, holiday.ifBlank { null })
        if (date.dayOfWeek != DayOfWeek.SUNDAY) return null
        return when (policy) {
            SundayPolicy.CLOSED -> DayClosure(date, ClosureReason.SUNDAY)
            SundayPolicy.TRADING_SUNDAYS ->
                if (calendar != null && date in calendar.tradingSundayDates) null else DayClosure(date, ClosureReason.NON_TRADING_SUNDAY)
            SundayPolicy.OPEN -> null
        }
    }

    /** The closed days of [year]/[month], by date. */
    fun closuresIn(year: Int, month: Int, policy: SundayPolicy, calendar: LegalCalendarYear?): Map<LocalDate, DayClosure> {
        if (policy == SundayPolicy.OPEN) return emptyMap()
        return generateSequence(LocalDate(year, month, 1)) { it.plus(DatePeriod(days = 1)) }
            .takeWhile { it.month.number == month }
            .mapNotNull { closure(it, policy, calendar) }
            .associateBy { it.date }
    }

    /** The trade-banned holiday's name ("" when unnamed), or null when [date] is none. */
    private fun holidayOn(date: LocalDate, calendar: LegalCalendarYear?): String? {
        if (calendar != null) return calendar.tradeBannedHoliday(date)?.name
        return if (PolishCalendar.isHoliday(date)) "" else null
    }
}
